import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalization } from '../../hooks/useLocalization';
import { getThumbnailUrl, getGalleryImageUrl } from '../../utils/imageUtils';

interface RoomImage {
    url: string;
    caption?: string;
    source?: string;
    isVerified?: boolean;
    index?: number;
}

interface RoomType {
    id: string;
    index: number;
    roomType: string;
    roomTypeId?: string;
    tourvisioName: string;
    description: string;
    shortDescription?: string;
    descriptionSv?: string;
    descriptionEn?: string;
    descriptionDa?: string;
    highlights: string[];
    amenities: { name: string; icon?: string }[];
    images: RoomImage[];
    roomDetails?: {
        maxAdults?: number;
        maxChildren?: number;
        maxOccupancy?: number;
    };
    isActive: boolean;
    sortOrder?: number;
    lastUpdated?: string;
}

interface BoardType {
    id: string;
    index: number;
    boardTypeId?: string;
    boardTypeName: string;
    boardTypeNameSv?: string;
    boardTypeNameEn?: string;
    boardTypeNameDa?: string;
    description?: string;
    isActive: boolean;
    sortOrder?: number;
}

interface HotelSearchResult {
    _id: string;
    hotelId: string;
    name: string;
    tourvisioId: string;
    city: string;
    country: string;
    stars: number;
    roomCount: number;
    boardCount: number;
    thumbnail: string;
    isActive: boolean;
}

interface SerpImage {
    url: string;
    thumbnail: string;
    title: string;
    source: string;
    width: number;
    height: number;
    index: number;
}

interface RoomMappingPanelProps {
    hotelId?: string;
    hotelName?: string;
    searchContext?: any;
}

const labels = {
    en: {
        title: 'Room Management',
        searchPlaceholder: 'Search hotels by name, city, or ID...',
        rooms: 'Rooms',
        boards: 'Board Types',
        fetchFromTourvisio: 'Fetch from Tourvisio',
        fetchImages: 'Fetch Room Images',
        approveImage: 'Add',
        addRoom: 'Add Room',
        editRoom: 'Edit Room',
        saveRoom: 'Save Room',
        deleteRoom: 'Delete Room',
        toggleActive: 'Toggle Active',
        active: 'Active',
        inactive: 'Inactive',
        noRooms: 'No rooms configured. Click "Fetch from Tourvisio" to discover rooms.',
        roomName: 'Room Type Name',
        description: 'Description',
        amenities: 'Amenities',
        images: 'Images',
        fetchingTourvisio: 'Fetching rooms from Tourvisio API...',
        fetchingImages: 'Searching for room images...',
        selectHotel: 'Select a Hotel',
        changeHotel: 'Change Hotel',
        serpImages: 'SerpAPI Images',
        noSerpImages: 'No images found. Try a different search.',
        addImageUrl: 'Add image URL',
        loading: 'Loading...',
        saved: 'Saved!',
        error: 'Error',
        cancel: 'Cancel',
        confirm: 'Confirm',
    },
    sv: {
        title: 'Rumshantering',
        searchPlaceholder: 'Sök hotell efter namn, stad eller ID...',
        rooms: 'Rum',
        boards: 'Pensionstyper',
        fetchFromTourvisio: 'Hämta från Tourvisio',
        fetchImages: 'Hämta rumsbilder',
        approveImage: 'Lägg till',
        addRoom: 'Lägg till rum',
        editRoom: 'Redigera rum',
        saveRoom: 'Spara rum',
        deleteRoom: 'Ta bort rum',
        toggleActive: 'Växla aktiv',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        noRooms: 'Inga rum konfigurerade. Klicka "Hämta från Tourvisio" för att upptäcka rum.',
        roomName: 'Rumstyp',
        description: 'Beskrivning',
        amenities: 'Bekvämligheter',
        images: 'Bilder',
        fetchingTourvisio: 'Hämtar rum från Tourvisio API...',
        fetchingImages: 'Söker efter rumsbilder...',
        selectHotel: 'Välj ett hotell',
        changeHotel: 'Byt hotell',
        serpImages: 'SerpAPI-bilder',
        noSerpImages: 'Inga bilder hittades. Prova en annan sökning.',
        addImageUrl: 'Lägg till bild-URL',
        loading: 'Laddar...',
        saved: 'Sparat!',
        error: 'Fel',
        cancel: 'Avbryt',
        confirm: 'Bekräfta',
    }
};

export const RoomMappingPanel: React.FC<RoomMappingPanelProps> = ({ hotelId: propHotelId, hotelName: propHotelName }) => {
    const { language } = useLocalization();
    const l = labels[language] || labels.en;

    // Hotel selection state
    const [hotelId, setHotelId] = useState<string>(propHotelId || '');
    const [hotelName, setHotelName] = useState<string>(propHotelName || '');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<HotelSearchResult[]>([]);
    const [searching, setSearching] = useState(false);

    // Room data state
    const [rooms, setRooms] = useState<RoomType[]>([]);
    const [boardTypes, setBoardTypes] = useState<BoardType[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<RoomType | null>(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Fetch/edit state
    const [fetchingTourvisio, setFetchingTourvisio] = useState(false);
    const [fetchingImages, setFetchingImages] = useState(false);
    const [serpImages, setSerpImages] = useState<SerpImage[]>([]);
    const [showSerpModal, setShowSerpModal] = useState(false);
    const [scrapingRoom, setScrapingRoom] = useState<number | null>(null);
    const [scrapingAll, setScrapingAll] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState<Partial<RoomType>>({});
    const [activeTab, setActiveTab] = useState<'rooms' | 'boards'>('rooms');
    const [newImageUrl, setNewImageUrl] = useState('');

    const searchTimeout = useRef<any>(null);

    // ── Auto-search hotels ──
    useEffect(() => {
        if (propHotelId) return; // Skip search if hotel provided via props
        if (searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(async () => {
            setSearching(true);
            try {
                const resp = await fetch(`/api/admin/rooms/search-hotels?q=${encodeURIComponent(searchQuery)}`);
                const data = await resp.json();
                if (data.success) setSearchResults(data.hotels || []);
            } catch (err) {
                console.error('Hotel search error:', err);
            }
            setSearching(false);
        }, 400);
    }, [searchQuery, propHotelId]);

    // ── Fetch rooms when hotelId changes ──
    const fetchRooms = useCallback(async () => {
        if (!hotelId) return;
        setLoading(true);
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}`);
            const data = await resp.json();
            if (data.success) {
                setRooms(data.rooms || []);
                setBoardTypes(data.boardTypes || []);
                if (data.hotel?.name) setHotelName(data.hotel.name);
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to fetch rooms' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
        setLoading(false);
    }, [hotelId]);

    useEffect(() => { fetchRooms(); }, [fetchRooms]);

    // ── Hotel selection ──
    const handleSelectHotel = (hotel: HotelSearchResult) => {
        setHotelId(hotel.hotelId || hotel.tourvisioId);
        setHotelName(hotel.name);
        setSearchResults([]);
        setSearchQuery('');
    };

    const handleChangeHotel = () => {
        setHotelId('');
        setHotelName('');
        setRooms([]);
        setBoardTypes([]);
        setSelectedRoom(null);
        setEditMode(false);
    };

    // ── Fetch from Tourvisio ──
    const fetchFromTourvisio = async () => {
        if (!hotelId) return;
        setFetchingTourvisio(true);
        setMessage(null);
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/fetch-from-tourvisio`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await resp.json();
            if (data.success) {
                setMessage({ type: 'success', text: `${data.addedRooms} new rooms discovered (${data.totalRooms} total)` });
                await fetchRooms();
            } else {
                setMessage({ type: 'error', text: data.message || 'Fetch failed' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
        setFetchingTourvisio(false);
    };

    // ── Toggle room ──
    const handleToggleRoom = async (room: RoomType) => {
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${room.index}/toggle`, {
                method: 'PATCH'
            });
            const data = await resp.json();
            if (data.success) {
                setRooms(prev => prev.map((r, i) =>
                    i === room.index ? { ...r, isActive: data.isActive } : r
                ));
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Toggle board ──
    const handleToggleBoard = async (board: BoardType) => {
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/board/${board.index}/toggle`, {
                method: 'PATCH'
            });
            const data = await resp.json();
            if (data.success) {
                setBoardTypes(prev => prev.map((b, i) =>
                    i === board.index ? { ...b, isActive: data.isActive } : b
                ));
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Delete room ──
    const handleDeleteRoom = async (room: RoomType) => {
        if (!window.confirm(`Delete "${room.roomType}"?`)) return;
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${room.index}`, {
                method: 'DELETE'
            });
            const data = await resp.json();
            if (data.success) {
                await fetchRooms();
                if (selectedRoom?.index === room.index) setSelectedRoom(null);
                setMessage({ type: 'success', text: data.message });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Save room ──
    const handleSaveRoom = async () => {
        if (!editData.roomType) return;
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    roomType: editData.roomType,
                    description: editData.description || '',
                    descriptionSv: editData.descriptionSv || editData.shortDescription || '',
                    descriptionEn: editData.descriptionEn || '',
                    descriptionDa: editData.descriptionDa || '',
                    highlights: editData.highlights || [],
                    amenities: editData.amenities || [],
                    images: editData.images || [],
                    isActive: editData.isActive !== false
                })
            });
            const data = await resp.json();
            if (data.success) {
                setMessage({ type: 'success', text: l.saved });
                setEditMode(false);
                await fetchRooms();
            } else {
                setMessage({ type: 'error', text: data.message });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Fetch SerpAPI images for a room ──
    const handleFetchImages = async (room: RoomType) => {
        setFetchingImages(true);
        setSerpImages([]);
        setShowSerpModal(true);
        setSelectedRoom(room);
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${room.index}/fetch-images`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await resp.json();
            if (data.success) {
                setSerpImages(data.images || []);
            } else {
                setMessage({ type: 'error', text: data.message || 'No images found' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
        setFetchingImages(false);
    };

    // ── Approve a SerpAPI image ──
    const handleApproveImage = async (img: SerpImage) => {
        if (!selectedRoom) return;
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${selectedRoom.index}/approve-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: img.url })
            });
            const data = await resp.json();
            if (data.success) {
                setMessage({ type: 'success', text: `Image added (${data.totalImages} total)` });
                setSerpImages(prev => prev.filter(i => i.url !== img.url));
                await fetchRooms();
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Add manual image URL ──
    const handleAddManualImage = async () => {
        if (!selectedRoom || !newImageUrl.trim()) return;
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${selectedRoom.index}/image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: newImageUrl.trim() })
            });
            const data = await resp.json();
            if (data.success) {
                setNewImageUrl('');
                await fetchRooms();
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Remove image ──
    const handleRemoveImage = async (room: RoomType, imageIndex: number) => {
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${room.index}/image/${imageIndex}`, {
                method: 'DELETE'
            });
            const data = await resp.json();
            if (data.success) await fetchRooms();
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    // ── Scrape room from Booking.com ──
    const handleScrapeBooking = async (room: RoomType) => {
        setScrapingRoom(room.index);
        setMessage(null);
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/room/${room.index}/scrape-booking`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await resp.json();
            if (data.success) {
                setMessage({ type: 'success', text: `Booking.com: ${data.result?.images?.length || 0} images scraped for "${room.roomType}"` });
                await fetchRooms();
            } else {
                setMessage({ type: 'error', text: data.message || 'No Booking.com data found' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
        setScrapingRoom(null);
    };

    // ── Bulk scrape all rooms from Booking.com ──
    const handleScrapeBookingAll = async () => {
        if (!window.confirm('Scrape all rooms from Booking.com? This may take a few minutes.')) return;
        setScrapingAll(true);
        setMessage(null);
        try {
            const resp = await fetch(`/api/admin/rooms/hotel/${hotelId}/scrape-booking-all`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await resp.json();
            if (data.success) {
                setMessage({ type: 'success', text: `Booking.com bulk scrape: ${data.scraped}/${data.total} rooms updated` });
                await fetchRooms();
            } else {
                setMessage({ type: 'error', text: data.message || 'Bulk scrape failed' });
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        }
        setScrapingAll(false);
    };

    // ── Start editing ──
    const startEdit = (room: RoomType) => {
        setSelectedRoom(room);
        setEditMode(true);
        setEditData({
            roomType: room.roomType,
            description: room.description,
            shortDescription: room.shortDescription,
            descriptionSv: room.descriptionSv,
            descriptionEn: room.descriptionEn,
            descriptionDa: room.descriptionDa,
            highlights: [...(room.highlights || [])],
            amenities: [...(room.amenities || [])],
            images: [...(room.images || [])],
            isActive: room.isActive
        });
    };

    // ── Start adding new room ──
    const startAddRoom = () => {
        setSelectedRoom(null);
        setEditMode(true);
        setEditData({
            roomType: '',
            description: '',
            highlights: [],
            amenities: [],
            images: [],
            isActive: true
        });
    };

    // ── Clear message ──
    useEffect(() => {
        if (message) {
            const t = setTimeout(() => setMessage(null), 4000);
            return () => clearTimeout(t);
        }
    }, [message]);

    // ════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════

    // ── Hotel Selection UI ──
    if (!hotelId) {
        return (
            <div className="p-4 md:p-6">
                <h2 className="text-xl font-bold text-slate-100 mb-4 flex items-center gap-2">
                    <span className="material-icons text-indigo-400">hotel</span>
                    {l.title}
                </h2>
                <p className="text-slate-400 text-sm mb-4">{l.selectHotel}</p>

                <div className="relative">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={l.searchPlaceholder}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-200
                                   placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                    />
                    {searching && (
                        <span className="absolute right-3 top-3.5 material-icons text-slate-500 animate-spin text-sm">sync</span>
                    )}
                </div>

                {searchResults.length > 0 && (
                    <div className="mt-2 bg-slate-800 border border-slate-600 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                        {searchResults.map(hotel => (
                            <button
                                key={hotel._id}
                                onClick={() => handleSelectHotel(hotel)}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700
                                           transition-colors text-left border-b border-slate-700 last:border-b-0"
                            >
                                {hotel.thumbnail ? (
                                    <img src={hotel.thumbnail} alt="" className="w-12 h-8 object-cover rounded" />
                                ) : (
                                    <div className="w-12 h-8 bg-slate-700 rounded flex items-center justify-center">
                                        <span className="material-icons text-slate-500 text-sm">image</span>
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-200 truncate">{hotel.name}</div>
                                    <div className="text-xs text-slate-400">
                                        {hotel.city}{hotel.country ? `, ${hotel.country}` : ''} • {'★'.repeat(hotel.stars)}
                                        {hotel.roomCount > 0 && ` • ${hotel.roomCount} rooms`}
                                    </div>
                                </div>
                                <span className="material-icons text-slate-500 text-sm">chevron_right</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // ── Main Panel ──
    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="material-icons text-indigo-400">hotel</span>
                    <div className="min-w-0">
                        <h2 className="text-lg font-bold text-slate-100 truncate">{hotelName}</h2>
                        <span className="text-xs text-slate-500">ID: {hotelId}</span>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {!propHotelId && (
                        <button onClick={handleChangeHotel}
                            className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors">
                            {l.changeHotel}
                        </button>
                    )}
                    <button onClick={fetchFromTourvisio}
                        disabled={fetchingTourvisio}
                        className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg
                                       transition-colors disabled:opacity-50 flex items-center gap-1">
                        <span className="material-icons text-sm">{fetchingTourvisio ? 'sync' : 'cloud_download'}</span>
                        {fetchingTourvisio ? l.fetchingTourvisio : l.fetchFromTourvisio}
                    </button>
                    <button onClick={startAddRoom}
                        className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-500 text-white rounded-lg
                                       transition-colors flex items-center gap-1">
                        <span className="material-icons text-sm">add</span>
                        {l.addRoom}
                    </button>
                    <button onClick={handleScrapeBookingAll}
                        disabled={scrapingAll || rooms.length === 0}
                        className="px-3 py-1.5 text-xs bg-orange-600 hover:bg-orange-500 text-white rounded-lg
                                       transition-colors disabled:opacity-50 flex items-center gap-1">
                        <span className={`material-icons text-sm ${scrapingAll ? 'animate-spin' : ''}`}>{scrapingAll ? 'sync' : 'travel_explore'}</span>
                        {scrapingAll ? 'Scraping...' : 'Scrape Booking.com'}
                    </button>
                </div>
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-700' :
                    'bg-red-900/50 text-red-300 border border-red-700'
                    }`}>
                    {message.text}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mb-4 bg-slate-800 rounded-lg p-1">
                <button onClick={() => setActiveTab('rooms')}
                    className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center gap-1
                            ${activeTab === 'rooms' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                    <span className="material-icons text-sm">bed</span>
                    {l.rooms} ({rooms.length})
                </button>
                <button onClick={() => setActiveTab('boards')}
                    className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-center gap-1
                            ${activeTab === 'boards' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
                    <span className="material-icons text-sm">restaurant</span>
                    {l.boards} ({boardTypes.length})
                </button>
            </div>

            {/* Loading */}
            {loading && <div className="text-center py-8 text-slate-400">{l.loading}</div>}

            {/* ── Rooms Tab ── */}
            {activeTab === 'rooms' && !loading && (
                <div className="space-y-2">
                    {rooms.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <span className="material-icons text-4xl mb-2 block">meeting_room</span>
                            {l.noRooms}
                        </div>
                    ) : (
                        rooms.map((room, idx) => (
                            <div key={room.id || idx}
                                className={`bg-slate-800 border rounded-lg p-3 transition-all ${room.isActive ? 'border-slate-700' : 'border-red-800/40 opacity-60'
                                    } ${selectedRoom?.index === idx ? 'ring-2 ring-indigo-500' : ''}`}>
                                <div className="flex items-center gap-3">
                                    {/* Room thumbnail */}
                                    <div className="w-16 h-12 rounded overflow-hidden flex-shrink-0 bg-slate-700">
                                        {room.images?.[0] ? (
                                            <img src={getThumbnailUrl(room.images[0].url, 200) || room.images[0].url} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <span className="material-icons text-slate-500 text-sm">image</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Room info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-slate-200 truncate">
                                                {room.roomType}
                                            </span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${room.isActive
                                                ? 'bg-green-900/50 text-green-400 border border-green-700'
                                                : 'bg-red-900/50 text-red-400 border border-red-700'
                                                }`}>
                                                {room.isActive ? l.active : l.inactive}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            {room.images?.length || 0} img • {room.amenities?.length || 0} amenities
                                            {room.roomDetails?.maxOccupancy ? ` • max ${room.roomDetails.maxOccupancy}` : ''}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button onClick={() => handleFetchImages(room)} title={l.fetchImages}
                                            className="p-1.5 bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 rounded transition-colors">
                                            <span className="material-icons text-sm">image_search</span>
                                        </button>
                                        <button onClick={() => handleScrapeBooking(room)} title="Scrape from Booking.com"
                                            disabled={scrapingRoom === room.index}
                                            className="p-1.5 bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 rounded transition-colors disabled:opacity-50">
                                            <span className={`material-icons text-sm ${scrapingRoom === room.index ? 'animate-spin' : ''}`}>
                                                {scrapingRoom === room.index ? 'sync' : 'travel_explore'}
                                            </span>
                                        </button>
                                        <button onClick={() => startEdit(room)} title={l.editRoom}
                                            className="p-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded transition-colors">
                                            <span className="material-icons text-sm">edit</span>
                                        </button>
                                        <button onClick={() => handleToggleRoom(room)} title={l.toggleActive}
                                            className={`p-1.5 rounded transition-colors ${room.isActive
                                                ? 'bg-orange-600/20 hover:bg-orange-600/40 text-orange-400'
                                                : 'bg-green-600/20 hover:bg-green-600/40 text-green-400'
                                                }`}>
                                            <span className="material-icons text-sm">
                                                {room.isActive ? 'visibility_off' : 'visibility'}
                                            </span>
                                        </button>
                                        <button onClick={() => handleDeleteRoom(room)} title={l.deleteRoom}
                                            className="p-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded transition-colors">
                                            <span className="material-icons text-sm">delete</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded images */}
                                {room.images && room.images.length > 0 && selectedRoom?.index === idx && (
                                    <div className="mt-3 pt-3 border-t border-slate-700">
                                        <div className="text-xs text-slate-400 mb-2">{l.images} ({room.images.length})</div>
                                        <div className="flex gap-2 overflow-x-auto pb-2">
                                            {room.images.map((img, imgIdx) => (
                                                <div key={imgIdx} className="relative flex-shrink-0 group">
                                                    <img src={getThumbnailUrl(img.url, 200) || img.url} alt="" className="h-16 w-24 object-cover rounded border border-slate-600" />
                                                    <button onClick={() => handleRemoveImage(room, imgIdx)}
                                                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full
                                                                       text-white flex items-center justify-center opacity-0
                                                                       group-hover:opacity-100 transition-opacity">
                                                        <span className="material-icons text-xs">close</span>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* ── Board Types Tab ── */}
            {activeTab === 'boards' && !loading && (
                <div className="space-y-2">
                    {boardTypes.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <span className="material-icons text-4xl mb-2 block">restaurant</span>
                            No board types. Fetch from Tourvisio to discover.
                        </div>
                    ) : (
                        boardTypes.map((board, idx) => (
                            <div key={board.id || idx}
                                className={`bg-slate-800 border rounded-lg p-3 flex items-center justify-between ${board.isActive ? 'border-slate-700' : 'border-red-800/40 opacity-60'
                                    }`}>
                                <div>
                                    <span className="text-sm font-medium text-slate-200">{board.boardTypeName}</span>
                                    {board.boardTypeNameSv && (
                                        <span className="text-xs text-slate-500 ml-2">({board.boardTypeNameSv})</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${board.isActive
                                        ? 'bg-green-900/50 text-green-400 border border-green-700'
                                        : 'bg-red-900/50 text-red-400 border border-red-700'
                                        }`}>
                                        {board.isActive ? l.active : l.inactive}
                                    </span>
                                    <button onClick={() => handleToggleBoard(board)}
                                        className={`p-1.5 rounded transition-colors ${board.isActive
                                            ? 'bg-orange-600/20 hover:bg-orange-600/40 text-orange-400'
                                            : 'bg-green-600/20 hover:bg-green-600/40 text-green-400'
                                            }`}>
                                        <span className="material-icons text-sm">
                                            {board.isActive ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* ── Edit Modal ── */}
            {editMode && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                    <div className="bg-slate-800 border border-slate-600 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6">
                        <h3 className="text-lg font-bold text-slate-100 mb-4">
                            {selectedRoom ? l.editRoom : l.addRoom}
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">{l.roomName} *</label>
                                <input type="text" value={editData.roomType || ''}
                                    onChange={e => setEditData(p => ({ ...p, roomType: e.target.value }))}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200
                                                  focus:outline-none focus:border-indigo-500"
                                    disabled={!!selectedRoom}
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">{l.description}</label>
                                <textarea value={editData.description || ''}
                                    onChange={e => setEditData(p => ({ ...p, description: e.target.value }))}
                                    rows={3}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200
                                                     focus:outline-none focus:border-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">{l.description} (EN)</label>
                                <textarea value={editData.descriptionEn || ''}
                                    onChange={e => setEditData(p => ({ ...p, descriptionEn: e.target.value }))}
                                    rows={2}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200
                                                     focus:outline-none focus:border-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">{l.description} (SV)</label>
                                <textarea value={editData.descriptionSv || editData.shortDescription || ''}
                                    onChange={e => setEditData(p => ({ ...p, descriptionSv: e.target.value }))}
                                    rows={2}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200
                                                     focus:outline-none focus:border-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">{l.description} (DA)</label>
                                <textarea value={editData.descriptionDa || ''}
                                    onChange={e => setEditData(p => ({ ...p, descriptionDa: e.target.value }))}
                                    rows={2}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200
                                                     focus:outline-none focus:border-indigo-500" />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <button onClick={() => { setEditMode(false); setSelectedRoom(null); }}
                                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm">
                                {l.cancel}
                            </button>
                            <button onClick={handleSaveRoom}
                                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm">
                                {l.saveRoom}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── SerpAPI Images Modal ── */}
            {showSerpModal && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                    <div className="bg-slate-800 border border-slate-600 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                                <span className="material-icons text-purple-400">image_search</span>
                                {l.serpImages}
                                {selectedRoom && <span className="text-sm text-slate-400 font-normal">— {selectedRoom.roomType}</span>}
                            </h3>
                            <button onClick={() => { setShowSerpModal(false); setSerpImages([]); }}
                                className="p-1 hover:bg-slate-700 rounded">
                                <span className="material-icons text-slate-400">close</span>
                            </button>
                        </div>

                        {/* Manual URL input */}
                        <div className="flex gap-2 mb-4">
                            <input type="text" value={newImageUrl}
                                onChange={e => setNewImageUrl(e.target.value)}
                                placeholder={l.addImageUrl}
                                className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-200
                                              focus:outline-none focus:border-indigo-500"
                            />
                            <button onClick={handleAddManualImage}
                                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm">
                                {l.approveImage}
                            </button>
                        </div>

                        {fetchingImages ? (
                            <div className="text-center py-8 text-slate-400">
                                <span className="material-icons animate-spin text-2xl mb-2 block">sync</span>
                                {l.fetchingImages}
                            </div>
                        ) : serpImages.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">{l.noSerpImages}</div>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {serpImages.map((img, idx) => (
                                    <div key={idx} className="relative group rounded-lg overflow-hidden bg-slate-700">
                                        <img src={img.thumbnail || img.url} alt={img.title || ''}
                                            className="w-full h-28 object-cover" loading="lazy" />
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100
                                                        transition-opacity flex items-center justify-center">
                                            <button onClick={() => handleApproveImage(img)}
                                                className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white
                                                               rounded-lg text-xs flex items-center gap-1">
                                                <span className="material-icons text-sm">check</span>
                                                {l.approveImage}
                                            </button>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                                            <span className="text-[10px] text-slate-300 truncate block">{img.title || img.source}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoomMappingPanel;
